import * as React from "react";
import { ImagePlus } from "lucide-react";

import { cn } from "../lib/utils";

export interface PhotoDropzoneProps {
  /** Called with the selected/dropped files. Pure capture — upload logic stays with the consumer. */
  onFiles: (files: File[]) => void;
  /** Accepted file types (input accept attribute). Defaults to images. */
  accept?: string;
  /** Allow selecting multiple files. */
  multiple?: boolean;
  /** Disables the dropzone. */
  disabled?: boolean;
  /** Primary label (e.g. "Drag photos here or click to upload"). */
  label?: string;
  /** Secondary hint (e.g. "PNG, JPG up to 10MB"). */
  hint?: string;
  className?: string;
}

function stop(e: React.SyntheticEvent) {
  e.preventDefault();
  e.stopPropagation();
}

/**
 * Industrial photo dropzone (Stitch "Industrial Management Portal", screen-03 L205-211).
 *
 * Reusable capture-only UI: accepts click + drag&drop and forwards `File[]` via `onFiles`.
 * No upload/storage wiring — that belongs to the consuming screen (Out of Scope per F4).
 *
 * Pixel mapping: `border-2 border-dashed border-outline-variant bg-surface-container-low
 * rounded-lg p-8` + a circular add icon.
 */
export function PhotoDropzone({
  onFiles,
  accept = "image/*",
  multiple = true,
  disabled = false,
  label,
  hint,
  className,
}: PhotoDropzoneProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = React.useState(false);

  const emit = (fileList: FileList | null) => {
    const files = fileList ? Array.from(fileList) : [];
    if (files.length > 0) onFiles(files);
  };

  const handleDrop = (e: React.DragEvent<HTMLButtonElement>) => {
    stop(e);
    setDragActive(false);
    if (!disabled) emit(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLButtonElement>) => {
    stop(e);
    setDragActive(!disabled);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLButtonElement>) => {
    stop(e);
    setDragActive(false);
  };

  return (
    <button
      type="button"
      disabled={disabled}
      data-testid="photo-dropzone"
      data-drag-active={String(dragActive)}
      onClick={() => inputRef.current?.click()}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={cn(
        "flex w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-outline-variant bg-surface-container-low p-8 text-center transition-colors hover:bg-surface-container disabled:cursor-not-allowed disabled:opacity-50",
        dragActive && "border-primary bg-surface-container",
        className,
      )}
    >
      <span className="flex h-12 w-12 items-center justify-center rounded-full border border-outline-variant bg-background">
        <ImagePlus className="h-6 w-6 text-primary" aria-hidden="true" />
      </span>
      {label && <span className="text-sm font-medium text-foreground">{label}</span>}
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        data-testid="photo-dropzone-input"
        onChange={(e) => {
          emit(e.target.files);
          e.target.value = "";
        }}
      />
    </button>
  );
}
